import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Download, Brain, TrendingUp, TrendingDown, DollarSign,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  BarChart3, PieChart, Activity, FileText, Users, Landmark,
  Building2, Scale, BookOpen, Calendar, ExternalLink
} from 'lucide-react';
import './SenateDisclosuresPage.css';

// =============================================================================
// MOCK DATA — Senate LDA filings (LD-1 registrations + LD-2 quarterly reports)
// =============================================================================

const mockDisclosures = [
  { id: 1, registrant: 'Akin Gump Strauss Hauer & Feld', client: 'Meta Platforms Inc', clientTicker: 'META', type: 'LD-2', amount: 2840000, quarter: 'Q4 2025', filedDate: '2026-01-20', issues: ['Telecom', 'Privacy', 'AI Regulation'], lobbyists: 4, specificBills: ['S.2142 - AI Transparency Act', 'H.R.3180 - Data Privacy Act'] },
  { id: 2, registrant: 'Brownstein Hyatt Farber Schreck', client: 'Amazon.com Inc', clientTicker: 'AMZN', type: 'LD-2', amount: 5620000, quarter: 'Q4 2025', filedDate: '2026-01-20', issues: ['Trade', 'Tax', 'Antitrust'], lobbyists: 7, specificBills: ['H.R.2768 - American Innovation Act', 'S.1847 - Digital Markets Act'] },
  { id: 3, registrant: 'Capitol Counsel LLC', client: 'Lockheed Martin Corp', clientTicker: 'LMT', type: 'LD-2', amount: 3410000, quarter: 'Q4 2025', filedDate: '2026-01-19', issues: ['Defense', 'Homeland Security', 'Intelligence'], lobbyists: 5, specificBills: ['H.R.4301 - FY2026 NDAA', 'S.2890 - Defense Modernization'] },
  { id: 4, registrant: 'Squire Patton Boggs', client: 'Alphabet Inc', clientTicker: 'GOOGL', type: 'LD-2', amount: 3180000, quarter: 'Q4 2025', filedDate: '2026-01-18', issues: ['AI Regulation', 'Copyright', 'Antitrust'], lobbyists: 6, specificBills: ['S.2142 - AI Transparency Act', 'H.R.4520 - Copyright Modernization'] },
  { id: 5, registrant: 'Holland & Knight LLP', client: 'Pfizer Inc', clientTicker: 'PFE', type: 'LD-2', amount: 4280000, quarter: 'Q4 2025', filedDate: '2026-01-18', issues: ['Healthcare', 'Medicare', 'Drug Pricing'], lobbyists: 8, specificBills: ['S.1920 - Prescription Drug Act', 'H.R.3456 - Medicare Reform'] },
  { id: 6, registrant: 'K&L Gates LLP', client: 'Exxon Mobil Corp', clientTicker: 'XOM', type: 'LD-2', amount: 2960000, quarter: 'Q4 2025', filedDate: '2026-01-17', issues: ['Energy', 'Environment', 'Tax'], lobbyists: 5, specificBills: ['H.R.2100 - Energy Independence Act', 'S.3340 - Carbon Tax Repeal'] },
  { id: 7, registrant: 'Covington & Burling LLP', client: 'JPMorgan Chase & Co', clientTicker: 'JPM', type: 'LD-2', amount: 3750000, quarter: 'Q4 2025', filedDate: '2026-01-17', issues: ['Banking', 'Financial Services', 'Crypto'], lobbyists: 6, specificBills: ['S.2445 - Digital Asset Act', 'H.R.1578 - Banking Modernization'] },
  { id: 8, registrant: 'Invariant LLC', client: 'Apple Inc', clientTicker: 'AAPL', type: 'LD-2', amount: 2450000, quarter: 'Q4 2025', filedDate: '2026-01-16', issues: ['Privacy', 'Antitrust', 'Trade'], lobbyists: 4, specificBills: ['H.R.3180 - Data Privacy Act', 'S.1847 - Digital Markets Act'] },
  { id: 9, registrant: 'BGR Group', client: 'Raytheon Technologies', clientTicker: 'RTX', type: 'LD-2', amount: 2180000, quarter: 'Q4 2025', filedDate: '2026-01-16', issues: ['Defense', 'Foreign Affairs', 'Trade'], lobbyists: 4, specificBills: ['H.R.4301 - FY2026 NDAA', 'S.3120 - Arms Export Control'] },
  { id: 10, registrant: 'Cassidy & Associates', client: 'Boeing Co', clientTicker: 'BA', type: 'LD-2', amount: 3620000, quarter: 'Q4 2025', filedDate: '2026-01-15', issues: ['Defense', 'Aviation', 'Trade'], lobbyists: 6, specificBills: ['H.R.4301 - FY2026 NDAA', 'S.2788 - FAA Reauthorization'] },
  { id: 11, registrant: 'Hogan Lovells US LLP', client: 'Microsoft Corp', clientTicker: 'MSFT', type: 'LD-2', amount: 2890000, quarter: 'Q4 2025', filedDate: '2026-01-15', issues: ['AI Regulation', 'Cybersecurity', 'Gov Contracts'], lobbyists: 5, specificBills: ['S.2142 - AI Transparency Act', 'H.R.4102 - Cyber Defense Act'] },
  { id: 12, registrant: 'Arnold & Porter Kaye Scholer', client: 'UnitedHealth Group', clientTicker: 'UNH', type: 'LD-2', amount: 4120000, quarter: 'Q4 2025', filedDate: '2026-01-14', issues: ['Healthcare', 'Medicare', 'Insurance'], lobbyists: 7, specificBills: ['S.1920 - Prescription Drug Act', 'H.R.2890 - Medicare Advantage'] },
  { id: 13, registrant: 'Williams & Jensen PLLC', client: 'Goldman Sachs Group', clientTicker: 'GS', type: 'LD-2', amount: 2340000, quarter: 'Q4 2025', filedDate: '2026-01-14', issues: ['Financial Services', 'Tax', 'Crypto'], lobbyists: 4, specificBills: ['S.2445 - Digital Asset Act', 'H.R.3670 - Capital Gains Reform'] },
  { id: 14, registrant: 'Mehlman Consulting', client: 'NVIDIA Corp', clientTicker: 'NVDA', type: 'LD-2', amount: 1980000, quarter: 'Q4 2025', filedDate: '2026-01-13', issues: ['AI Regulation', 'Export Controls', 'Trade'], lobbyists: 3, specificBills: ['S.2142 - AI Transparency Act', 'H.R.4890 - Chip Export Act'] },
  { id: 15, registrant: 'Pillsbury Winthrop Shaw Pittman', client: 'Chevron Corp', clientTicker: 'CVX', type: 'LD-2', amount: 2710000, quarter: 'Q4 2025', filedDate: '2026-01-13', issues: ['Energy', 'Environment', 'Trade'], lobbyists: 5, specificBills: ['H.R.2100 - Energy Independence Act', 'S.2456 - LNG Export Act'] },
  { id: 16, registrant: 'Franklin Square Group', client: 'Northrop Grumman', clientTicker: 'NOC', type: 'LD-1', amount: 0, quarter: 'Q4 2025', filedDate: '2026-01-12', issues: ['Defense', 'Space', 'Cybersecurity'], lobbyists: 3, specificBills: [] },
  { id: 17, registrant: 'DLA Piper LLP', client: 'Comcast Corp', clientTicker: 'CMCSA', type: 'LD-2', amount: 2560000, quarter: 'Q4 2025', filedDate: '2026-01-12', issues: ['Telecom', 'Copyright', 'Broadband'], lobbyists: 5, specificBills: ['H.R.3180 - Data Privacy Act', 'S.3450 - Broadband Expansion'] },
  { id: 18, registrant: 'Steptoe & Johnson LLP', client: 'AT&T Inc', clientTicker: 'T', type: 'LD-2', amount: 2280000, quarter: 'Q4 2025', filedDate: '2026-01-11', issues: ['Telecom', 'Broadband', 'Privacy'], lobbyists: 4, specificBills: ['S.3450 - Broadband Expansion', 'H.R.3180 - Data Privacy Act'] },
  { id: 19, registrant: 'Jones Day', client: 'Citigroup Inc', clientTicker: 'C', type: 'LD-2', amount: 1890000, quarter: 'Q4 2025', filedDate: '2026-01-10', issues: ['Banking', 'Financial Services', 'Tax'], lobbyists: 3, specificBills: ['H.R.1578 - Banking Modernization', 'S.2890 - Tax Code Reform'] },
  { id: 20, registrant: 'Ballard Partners', client: 'Johnson & Johnson', clientTicker: 'JNJ', type: 'LD-2', amount: 1640000, quarter: 'Q4 2025', filedDate: '2026-01-10', issues: ['Healthcare', 'Drug Pricing', 'FDA'], lobbyists: 3, specificBills: ['S.1920 - Prescription Drug Act', 'H.R.4210 - FDA Modernization'] },
];

// Most lobbied issues
const mockTopIssues = [
  { issue: 'AI Regulation', filings: 48, amount: 28400000 },
  { issue: 'Healthcare', filings: 42, amount: 34200000 },
  { issue: 'Defense', filings: 38, amount: 31800000 },
  { issue: 'Financial Services', filings: 35, amount: 22600000 },
  { issue: 'Privacy / Data', filings: 32, amount: 18900000 },
  { issue: 'Trade', filings: 28, amount: 15400000 },
  { issue: 'Energy', filings: 26, amount: 19200000 },
  { issue: 'Antitrust', filings: 22, amount: 14800000 },
];

// Most lobbied bills
const mockHotBills = [
  { bill: 'S.2142', title: 'AI Transparency Act', lobbyers: 24, amount: 12800000 },
  { bill: 'H.R.4301', title: 'FY2026 NDAA', lobbyers: 21, amount: 18400000 },
  { bill: 'H.R.3180', title: 'Data Privacy Act', lobbyers: 18, amount: 9200000 },
  { bill: 'S.1920', title: 'Prescription Drug Act', lobbyers: 16, amount: 14600000 },
  { bill: 'S.2445', title: 'Digital Asset Act', lobbyers: 14, amount: 8400000 },
];

// Top lobbying firms
const mockTopFirms = [
  { firm: 'Akin Gump Strauss Hauer', clients: 142, revenue: 82400000 },
  { firm: 'Brownstein Hyatt Farber', clients: 128, revenue: 74200000 },
  { firm: 'Holland & Knight', clients: 115, revenue: 68900000 },
  { firm: 'Squire Patton Boggs', clients: 108, revenue: 62100000 },
  { firm: 'Covington & Burling', clients: 95, revenue: 58400000 },
];

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const formatNumber = (num) => num?.toLocaleString() ?? '—';

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function AIInsightCard({ insight }) {
  return (
    <div className="sd-ai-insight">
      <div className="sd-ai-header">
        <div className="sd-ai-badge"><Brain size={14} /> AI Insight</div>
        <span className="sd-ai-updated">Updated 4 hours ago</span>
      </div>
      <p className="sd-ai-text" dangerouslySetInnerHTML={{ __html: insight }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, subType = 'neutral', color }) {
  return (
    <div className="sd-stat-card" style={{ background: color }}>
      <div className="sd-stat-icon"><Icon size={20} /></div>
      <div className="sd-stat-label">{label}</div>
      <div className="sd-stat-value">{value}</div>
      <div className={`sd-stat-sub ${subType}`}>{sub}</div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function SenateDisclosuresPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [issueFilter, setIssueFilter] = useState('All');
  const [sortBy, setSortBy] = useState('filedDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const typeFilters = ['All', 'LD-2', 'LD-1'];
  const issueFilters = ['All', 'AI Regulation', 'Healthcare', 'Defense', 'Financial Services', 'Energy', 'Privacy'];

  const filteredDisclosures = useMemo(() => {
    let data = [...mockDisclosures];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(d =>
        d.registrant.toLowerCase().includes(q) ||
        d.client.toLowerCase().includes(q) ||
        d.clientTicker.toLowerCase().includes(q) ||
        d.issues.some(i => i.toLowerCase().includes(q)) ||
        d.specificBills.some(b => b.toLowerCase().includes(q))
      );
    }
    if (typeFilter !== 'All') data = data.filter(d => d.type === typeFilter);
    if (issueFilter !== 'All') data = data.filter(d => d.issues.some(i => i.toLowerCase().includes(issueFilter.toLowerCase())));

    data.sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortBy === 'registrant') return order * a.registrant.localeCompare(b.registrant);
      if (sortBy === 'client') return order * a.client.localeCompare(b.client);
      if (sortBy === 'amount') return order * (a.amount - b.amount);
      if (sortBy === 'filedDate') return order * a.filedDate.localeCompare(b.filedDate);
      if (sortBy === 'lobbyists') return order * (a.lobbyists - b.lobbyists);
      return 0;
    });
    return data;
  }, [searchTerm, typeFilter, issueFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredDisclosures.length / perPage);
  const pagedDisclosures = filteredDisclosures.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const stats = useMemo(() => {
    const totalSpend = mockDisclosures.reduce((s, d) => s + d.amount, 0);
    const totalFilings = mockDisclosures.length;
    const totalLobbyists = mockDisclosures.reduce((s, d) => s + d.lobbyists, 0);
    const ld2Count = mockDisclosures.filter(d => d.type === 'LD-2').length;
    return { totalSpend, totalFilings, totalLobbyists, ld2Count };
  }, []);

  const aiInsight = `<span class="highlight">AI regulation is now the #1 lobbied issue</span> surpassing healthcare for the first time in Q4 2025. The <span class="highlight">AI Transparency Act (S.2142)</span> attracted 24 lobbying clients spending $12.8M — tech giants <span class="highlight">Meta</span>, <span class="highlight">Alphabet</span>, <span class="highlight">Microsoft</span>, and <span class="highlight">NVIDIA</span> all filed on it. Defense spending filings are up 18% QoQ with the FY2026 NDAA drawing 21 lobbying clients. <span class="highlight">Amazon</span> leads all clients at $5.62M in Q4 lobbying expenditure.`;

  return (
    <div className="senate-disclosures-page">
      {/* Header */}
      <div className="sd-page-header">
        <div className="sd-header-content">
          <div className="sd-header-title">
            <div className="sd-header-icon"><Scale size={22} color="#fff" /></div>
            <div>
              <h1>Senate Disclosures</h1>
              <p>Lobbying Disclosure Act filings from the Senate Office of Public Records (LD-1 / LD-2)</p>
            </div>
          </div>
          <div className="sd-header-actions">
            <button className="sd-header-btn"><Download size={14} /> Export</button>
            <button className="sd-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <AIInsightCard insight={aiInsight} />

      {/* Stats Row */}
      <div className="sd-stats-row">
        <StatCard icon={DollarSign} label="Q4 Lobbying Spend" value={formatCurrency(stats.totalSpend)} sub={`${stats.ld2Count} quarterly reports`} subType="neutral" color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))" />
        <StatCard icon={FileText} label="Total Filings" value={stats.totalFilings.toString()} sub="LD-1 registrations + LD-2 reports" subType="neutral" color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))" />
        <StatCard icon={Users} label="Lobbyists Deployed" value={stats.totalLobbyists.toString()} sub="Across all tracked filings" subType="neutral" color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))" />
        <StatCard icon={BookOpen} label="Top Issue" value="AI Regulation" sub="48 filings, $28.4M spent" subType="neutral" color="linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(109, 40, 217, 0.2))" />
      </div>

      {/* Content Grid */}
      <div className="sd-content-grid">
        {/* Main Table */}
        <div className="sd-table-panel">
          <div className="sd-panel-header">
            <h2><Scale size={16} /> LDA Filings <span className="sd-record-count">{filteredDisclosures.length}</span></h2>
          </div>

          {/* Toolbar */}
          <div className="sd-toolbar">
            <div className="sd-search-box">
              <Search size={14} />
              <input type="text" placeholder="Search firm, client, ticker, issue, or bill..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="sd-filter-group">
              <div className="sd-filter-toggles">
                {typeFilters.map(f => (
                  <button key={f} className={`sd-filter-btn ${typeFilter === f ? 'active' : ''}`} onClick={() => { setTypeFilter(f); setCurrentPage(1); }}>{f}</button>
                ))}
              </div>
              <div className="sd-filter-toggles secondary">
                {issueFilters.map(f => (
                  <button key={f} className={`sd-filter-btn ${issueFilter === f ? 'active' : ''}`} onClick={() => { setIssueFilter(f); setCurrentPage(1); }}>{f}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Table */}
          {filteredDisclosures.length === 0 ? (
            <div className="sd-empty-state"><Search size={40} /><p>No filings match your filters</p></div>
          ) : (
            <div className="sd-table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('client')}>Client <SortIcon field="client" /></th>
                    <th onClick={() => handleSort('registrant')}>Lobbying Firm <SortIcon field="registrant" /></th>
                    <th>Type</th>
                    <th onClick={() => handleSort('amount')}>Amount <SortIcon field="amount" /></th>
                    <th>Issues</th>
                    <th onClick={() => handleSort('lobbyists')}>Lobbyists <SortIcon field="lobbyists" /></th>
                    <th onClick={() => handleSort('filedDate')}>Filed <SortIcon field="filedDate" /></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDisclosures.map(d => (
                    <tr key={d.id} onClick={() => navigate(`/stock/${d.clientTicker}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        <span className="sd-client-name">{d.client}</span>
                        <span className="sd-client-ticker">{d.clientTicker}</span>
                      </td>
                      <td>
                        <span className="sd-firm-name">{d.registrant}</span>
                      </td>
                      <td>
                        <span className={`sd-type-badge ${d.type === 'LD-1' ? 'registration' : 'quarterly'}`}>
                          {d.type}
                        </span>
                      </td>
                      <td><span className="sd-amount">{d.amount > 0 ? formatCurrency(d.amount) : 'New Reg'}</span></td>
                      <td>
                        <div className="sd-issues-cell">
                          {d.issues.slice(0, 2).map((issue, idx) => (
                            <span key={idx} className="sd-issue-tag">{issue}</span>
                          ))}
                          {d.issues.length > 2 && <span className="sd-issue-more">+{d.issues.length - 2}</span>}
                        </div>
                      </td>
                      <td><span className="sd-lobbyist-count">{d.lobbyists}</span></td>
                      <td>{new Date(d.filedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                      <td>
                        <button className="sd-chart-btn" onClick={(e) => { e.stopPropagation(); navigate(`/chart/${d.clientTicker}`); }} title={`Chart ${d.clientTicker}`}>
                          <BarChart3 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="sd-pagination">
              <span className="sd-pagination-info">Showing {pagedDisclosures.length} of {filteredDisclosures.length} filings</span>
              <div className="sd-pagination-btns">
                <button className="sd-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={14} /></button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i + 1} className={`sd-page-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                ))}
                <button className="sd-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="sd-sidebar">
          {/* Top Issues */}
          <div className="sd-sidebar-card">
            <div className="sd-card-header">
              <h3><BookOpen size={16} color="#a78bfa" /> Most Lobbied Issues</h3>
            </div>
            <div className="sd-issue-list">
              {mockTopIssues.map((item, i) => (
                <div className="sd-issue-row" key={i}>
                  <span className="sd-issue-name">{item.issue}</span>
                  <div className="sd-issue-bar-wrap">
                    <div className="sd-issue-bar" style={{ width: `${(item.filings / mockTopIssues[0].filings) * 100}%` }} />
                  </div>
                  <span className="sd-issue-count">{item.filings}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hot Bills */}
          <div className="sd-sidebar-card">
            <div className="sd-card-header">
              <h3><Landmark size={16} color="#f59e0b" /> Most Lobbied Bills</h3>
            </div>
            <div className="sd-bill-list">
              {mockHotBills.map((b, i) => (
                <div className="sd-bill-item" key={i}>
                  <div className="sd-bill-id">{b.bill}</div>
                  <div className="sd-bill-info">
                    <span className="sd-bill-title">{b.title}</span>
                    <span className="sd-bill-meta">{b.lobbyers} clients · {formatCurrency(b.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Firms */}
          <div className="sd-sidebar-card">
            <div className="sd-card-header">
              <h3><Building2 size={16} color="#10b981" /> Top Lobbying Firms</h3>
            </div>
            <div className="sd-firm-list">
              {mockTopFirms.map((f, i) => (
                <div className="sd-firm-item" key={i}>
                  <span className={`sd-firm-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : ''}`}>{i + 1}</span>
                  <div className="sd-firm-info">
                    <span className="sd-firm-nm">{f.firm}</span>
                    <span className="sd-firm-meta">{f.clients} clients</span>
                  </div>
                  <span className="sd-firm-revenue">{formatCurrency(f.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}